<template>
  <v-container fluid class="pa-3 pt-0" :class="{ 'global-follow-up-container': globalMode }">
    <ConfirmationModal
      ref="deleteConfirmation"
      title="Delete Follow-Up Action"
      subtitle=""
      message="Are you sure you want to delete this follow-up action?"
      confirm-text="Delete"
      deny-text="Cancel"
      confirm-color="error"
    />
    <ConfirmationModal
      ref="cancelAssignmentConfirmation"
      title="Cancel Follow-Up Action"
      message="Cancel this follow-up action? It will be moved to History."
      confirm-text="Cancel Action"
      deny-text="Keep Action"
      confirm-color="error"
    />
    <ConfirmationModal
      ref="deleteCommentConfirmation"
      title="Delete Comment"
      subtitle=""
      message="Are you sure you want to delete this comment?"
      confirm-text="Delete"
      deny-text="Cancel"
      confirm-color="error"
    />

    <v-card flat class="rounded-lg follow-up-card">
      <v-card-title class="d-flex align-center" :class="globalMode ? 'manage-header px-4 py-5' : 'px-4 pt-3 pb-3'">
        <div class="min-width-0">
          <div :class="globalMode ? 'text-h5 font-weight-bold manage-title' : 'text-h6 font-weight-bold'">
            {{ globalMode ? 'Manage Follow-Up Actions' : 'Follow-Up Actions' }}
          </div>
          <div class="body-2 font-weight-regular grey--text text--darken-1 mt-1">
            {{ globalMode ? 'Manage your actions across all modules.' : 'Track actions, assignments, and updates for this case.' }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          class="text-capitalize manage-create-button"
          :disabled="readOnly || !options.actionTypes.length"
          @click="openAddDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ globalMode ? 'Create Action' : 'Create' }}
        </v-btn>
        <v-btn v-if="globalMode" icon class="ml-3 manage-close-button" aria-label="Close Manage Follow-Up Actions" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <template v-if="globalMode">
        <div class="manage-tabs-row">
          <v-tabs v-model="manageQueue" color="primary" show-arrows class="manage-queue-tabs" data-cy="follow-up-queues">
            <v-tab v-for="queue in queues" :key="queue.value" :href="`#${queue.value}`" class="text-capitalize" :data-cy="`follow-up-tab-${queue.value}`">
              {{ queue.text }}
              <v-chip x-small class="ml-2" :color="queue.value === 'incoming' && queueCounts.incoming ? 'orange darken-2' : undefined" :dark="queue.value === 'incoming' && !!queueCounts.incoming">{{ queueCounts[queue.value] || 0 }}</v-chip>
            </v-tab>
          </v-tabs>
          <p class="manage-active-message">You have {{ activeActionCount }} active {{ activeActionCount === 1 ? 'action' : 'actions' }}</p>
        </div>
        <div class="caption grey--text text--darken-1 px-4 py-2">{{ currentQueue.description }}</div>
      </template>

      <div v-if="globalMode" class="manage-toolbar d-flex align-center px-4 py-3">
        <v-text-field
          v-model="manageSearch"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search actions, modules, or records"
          aria-label="Search follow-up actions"
          clearable
          dense
          outlined
          hide-details
          class="manage-search"
        />
        <v-spacer />
        <v-btn outlined color="primary" class="text-capitalize manage-filter-button ml-4" @click="manageFiltersOpen = true">
          <v-icon left small>mdi-filter-variant</v-icon>
          Filters{{ manageFilterCount ? ` (${manageFilterCount})` : '' }}
        </v-btn>
        <span class="caption grey--text text--darken-1 ml-4">
          {{ manageTotal }} {{ manageTotal === 1 ? 'result' : 'results' }} found
        </span>
      </div>

      <v-alert v-if="error" type="error" text class="ma-4">
        {{ error }}
        <v-btn text small color="error" class="ml-2 text-capitalize" @click="load">
          Try again
        </v-btn>
      </v-alert>

      <v-alert
        v-else-if="!loading && !options.enabled"
        type="info"
        text
        class="ma-4"
      >
        Follow-Up Actions are not enabled{{ globalMode ? '.' : ' for Delinquency.' }}
      </v-alert>

      <v-alert
        v-else-if="!loading && !options.actionTypes.length"
        type="info"
        text
        class="ma-4"
      >
        No active Follow-Up Action Types are configured{{ globalMode ? '.' : ' for Delinquency.' }}
      </v-alert>

      <v-data-table
        v-if="options.enabled"
        :headers="tableHeaders"
        :items="displayActions"
        :loading="loading"
        :options.sync="manageTableOptions"
        :server-items-length="globalMode ? manageTotal : -1"
        :fixed-header="globalMode"
        :footer-props="globalMode ? { 'items-per-page-options': [10, 25, 50] } : undefined"
        class="follow-up-table"
        loading-text="Loading Follow-Up Actions..."
        :no-data-text="globalMode ? (manageSearch || manageFilterCount ? 'No actions match your search or filters.' : `No actions in ${currentQueue.text}.`) : 'No Follow-Up Actions have been added to this case.'"
      >
        <template #[`item.assignment`]='{ item }'>
          <div class="py-2 assignment-cell">
            <div>{{ item.assignmentStatus === 'rejected' ? 'Returned to ' : '' }}{{ userName(item.assignedTo) }}</div>
            <div class="caption grey--text text--darken-1">From {{ userName(item.createdBy) }}</div>
            <v-chip x-small label class="mt-1" :color="assignmentColor(item)" dark>{{ assignmentLabel(item) }}</v-chip>
          </div>
        </template>
        <template #[`item.rejectionReason`]='{ item }'>
          <div class="body-2 assignment-reason">{{ item.rejectionReason }}</div>
        </template>
        <template #[`item.module`]='{ item }'>
          <div class="manage-module-list py-2">
            <v-chip
              v-for="moduleId in item.moduleIds?.length ? item.moduleIds : [item.module]"
              :key="moduleId"
              small
              outlined
              color="primary"
              class="font-weight-medium manage-module-chip"
            >
              {{ moduleName(moduleId) }}
            </v-chip>
          </div>
        </template>

        <template #[`item.relatedRecordLabel`]='{ item }'>
          {{ item.relatedRecordType === 'global' ? 'None' : item.relatedRecordLabel }}
        </template>
        <template #[`item.actionType`]="{ item }">
          <div class="py-3 action-name-cell">
            <div class="font-weight-medium grey--text text--darken-4">
              {{ item.actionType.name }}
            </div>
            <v-tooltip v-if="item.actionType.description" bottom max-width="420">
              <template #activator="{ on, attrs }">
                <div
                  class="caption grey--text text--darken-1 mt-1 action-description"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.actionType.description }}
                </div>
              </template>
              <span>{{ item.actionType.description }}</span>
            </v-tooltip>
          </div>
        </template>

        <template #[`item.priority`]="{ item }">
          <v-chip small label dark :color="priorityColor(item.priority.id)">
            {{ item.priority.name }}
          </v-chip>
        </template>

        <template #[`item.dueAt`]="{ item }">
          <div :class="{ 'error--text font-weight-medium': isOverdue(item) }">
            {{ formatDate(item.dueAt, 'MMM D, YYYY') }}
          </div>
          <div class="caption grey--text text--darken-1">
            {{ formatDate(item.dueAt, 'h:mm A') }}
            <span v-if="isOverdue(item)" class="error--text ml-1">· Overdue</span>
          </div>
        </template>

        <template #[`item.status`]="{ item }">
          <v-menu v-if="canEdit(item)" offset-y>
            <template #activator="{ on, attrs }">
              <v-chip
                small
                label
                dark
                :color="statusColor(item.status)"
                v-bind="attrs"
                v-on="on"
              >
                {{ statusName(item.status) }}
                <v-icon x-small right>mdi-chevron-down</v-icon>
              </v-chip>
            </template>
            <v-list dense>
              <v-list-item
                v-for="status in statuses"
                :key="status.value"
                :disabled="savingStatusId === item.id || status.value === item.status"
                @click="changeStatus(item, status.value)"
              >
                <v-list-item-icon class="mr-3">
                  <v-icon small :color="statusColor(status.value)">mdi-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ status.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-chip v-else small label dark :color="statusColor(item.status)">
            {{ statusName(item.status) }}
          </v-chip>
        </template>

        <template #[`item.comments`]="{ item }">
          <v-btn text small color="primary" class="text-capitalize" @click="openComments(item)">
            <v-icon small left>mdi-comment-text-outline</v-icon>
            {{ item.comments.length }} {{ item.comments.length === 1 ? 'Comment' : 'Comments' }}
          </v-btn>
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end align-center flex-wrap assignment-row-actions">
            <template v-if="canRespond(item)">
              <v-btn small depressed color="primary" class="text-capitalize" :loading="savingAssignmentId === item.id" :disabled="!!savingAssignmentId" @click="respondAssignment({ action: item, response: 'accept' })" data-cy="follow-up-accept">Accept</v-btn>
              <v-btn small outlined color="error" class="text-capitalize" :disabled="!!savingAssignmentId" @click="openAssignment(item, 'reject')" data-cy="follow-up-reject">Reject</v-btn>
            </template>
            <v-menu v-if="canReassign(item)" offset-y left min-width="220">
              <template #activator="{ on, attrs }">
                <v-btn small depressed color="primary" class="text-capitalize" :loading="savingAssignmentId === item.id" :disabled="!!savingAssignmentId" aria-label="Assignment options" v-bind="attrs" v-on="on" data-cy="follow-up-reassign-menu">
                  Assign <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item :disabled="!!savingAssignmentId" @click="openAssignment(item, 'assign')" data-cy="follow-up-reassign-option">
                  <v-list-item-icon class="mr-3"><v-icon small color="primary">mdi-account-switch-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>Reassign</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="!!savingAssignmentId" @click="assignAction({ action: item, assignedToId: user.id, assignmentNote: '' })" data-cy="follow-up-assign-self-option">
                  <v-list-item-icon class="mr-3"><v-icon small color="primary">mdi-account-check-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>Assign to myself</v-list-item-title>
                </v-list-item>
                <v-divider v-if="canCancelAssignment(item)" />
                <v-list-item v-if="canCancelAssignment(item)" :disabled="!!savingAssignmentId" @click="confirmCancelAssignment(item)" data-cy="follow-up-cancel-rejected">
                  <v-list-item-icon class="mr-3"><v-icon small color="error">mdi-cancel</v-icon></v-list-item-icon>
                  <v-list-item-title class="error--text">Cancel action</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn v-if="canCancelAssignment(item) && !canReassign(item)" small outlined color="error" class="text-capitalize" :loading="savingAssignmentId === item.id" :disabled="!!savingAssignmentId" @click="confirmCancelAssignment(item)" data-cy="follow-up-cancel-sent">Cancel</v-btn>
            <div class="assignment-utility-actions">
              <v-btn icon small color="primary" title="Assignment Details" aria-label="Assignment Details" @click="openAssignment(item)"><v-icon small>mdi-account-arrow-right-outline</v-icon></v-btn>
              <v-btn v-if="globalMode && item.relatedRecordType === 'delinquency_case'" icon small color="primary" title="View Details" @click="$emit('view-details', item)">
                <v-icon small>mdi-open-in-new</v-icon>
              </v-btn>
              <v-btn v-if="canEdit(item)" icon small color="primary" title="Edit" @click="openEditDialog(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn v-if="canEdit(item)" icon small color="error" title="Delete" @click="confirmDelete(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
      </v-data-table>
      <div v-if="globalMode && manageFiltersOpen" class="manage-filter-scrim" @click="manageFiltersOpen = false" />
      <transition name="manage-filter-slide">
      <aside v-if="globalMode && manageFiltersOpen" class="manage-filter-drawer" role="dialog" aria-label="Follow-Up Action filters">
        <div class="manage-filter-header d-flex align-center px-6 py-5">
          <div>
            <div class="text-h6 font-weight-bold manage-title">Filters</div>
            <div class="body-2 grey--text text--darken-1 mt-1">Refine the actions in this table</div>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Close filters" @click="manageFiltersOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="manage-filter-body px-6 py-6">
          <v-select
            v-model="manageFilters.module"
            :items="manageModuleOptions"
            item-text="name"
            item-value="id"
            label="Module"
            placeholder="All modules"
            multiple
            small-chips
            deletable-chips
            clearable
            dense
            outlined
            persistent-placeholder
            class="mb-4"
          />
          <v-select
            v-model="manageFilters.actionType"
            :items="manageActionTypeOptions"
            item-text="name"
            item-value="id"
            label="Action Type"
            placeholder="All action types"
            multiple
            small-chips
            deletable-chips
            clearable
            dense
            outlined
            persistent-placeholder
            class="mb-4"
          />
          <v-select
            v-model="manageFilters.status"
            :items="statuses"
            label="Status"
            placeholder="All statuses"
            multiple
            small-chips
            deletable-chips
            clearable
            dense
            outlined
            persistent-placeholder
            class="mb-4"
          />
          <v-select
            v-model="manageFilters.priority"
            :items="priorityOptions"
            item-text="name"
            item-value="id"
            label="Priority"
            placeholder="All priorities"
            multiple
            small-chips
            deletable-chips
            clearable
            dense
            outlined
            persistent-placeholder
            class="mb-4"
          />
          <v-select
            v-model="manageFilters.dueDate"
            :items="manageDueDateOptions"
            label="Due Date"
            placeholder="Any due date"
            multiple
            small-chips
            deletable-chips
            clearable
            dense
            outlined
            persistent-placeholder
            class="mb-2"
          />
          <div v-if="(manageFilters.dueDate || []).includes('custom')" class="manage-filter-date-range">
            <v-text-field v-model="manageFilters.dueFrom" type="date" label="From" dense outlined hide-details />
            <v-text-field v-model="manageFilters.dueTo" type="date" label="To" dense outlined hide-details />
          </div>
        </div>
        <div class="manage-filter-footer d-flex align-center px-6 py-4">
          <v-btn text color="primary" class="text-capitalize" :disabled="!manageFilterCount" @click="clearManageFilters">Clear Filters</v-btn>
          <v-spacer />
          <v-btn color="primary" depressed class="text-capitalize" @click="manageFiltersOpen = false">Show {{ manageTotal }} actions</v-btn>
        </div>
      </aside>
      </transition>
    </v-card>

    <v-dialog v-model="createDialog" :max-width="globalMode ? 1600 : 1400" persistent>
      <v-card>
        <v-card-title class="pb-1">Create Follow-Up Actions</v-card-title>
        <v-card-subtitle class="pt-1">
          {{ globalMode ? 'Select actions and their applicable modules.' : 'Select the follow-up actions you want to add to this Delinquency Case.' }}
        </v-card-subtitle>
        <v-card-text>
          <v-alert v-if="formError" type="error" text dense>{{ formError }}</v-alert>
          <v-alert v-if="assigneesError" type="error" text dense>{{ assigneesError }} <v-btn text small color="error" @click="loadAssignees">Try again</v-btn></v-alert>
          <v-data-table
            v-model="selectedSettings"
            :headers="creationHeaders"
            :items="creationRows"
            item-key="id"
            show-select
            hide-default-footer
            :items-per-page="-1"
            :mobile-breakpoint="1280"
            class="selection-table"
            :no-data-text="globalMode ? 'No active actions are configured.' : 'No active actions are configured for Delinquency.'"
          >
            <template #[`item.name`]="{ item }">
              <div class="py-3 selection-name-cell">
                <div class="font-weight-medium">{{ item.name }}</div>
                <v-tooltip v-if="item.description" bottom max-width="420">
                  <template #activator="{ on, attrs }">
                    <div
                      class="caption grey--text mt-1 selection-description"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ descriptionPreview(item.description) }}
                    </div>
                  </template>
                  <span>{{ item.description }}</span>
                </v-tooltip>
              </div>
            </template>
            <template #[`item.selectedModuleIds`]="{ item }">
              <v-select
                v-model="item.selectedModuleIds"
                :items="applicableModules(item)"
                item-text="name"
                item-value="id"
                multiple
                small-chips
                deletable-chips
                dense
                outlined
                hide-details
                placeholder="Select modules"
                aria-label="Modules"
                class="my-2"
              />
            </template>
            <template #[`item.assignedToId`]="{ item }">
              <v-autocomplete v-model="item.assignedToId" :items="assigneeOptions" item-text="name" item-value="id" :loading="assigneesLoading" label="Assign to" dense outlined hide-details class="my-2" data-cy="follow-up-create-assignee" />
              <div class="caption grey--text">{{ item.assignedToId === user.id ? 'Added to My Actions as Pending' : 'Requires acceptance' }}</div>
            </template>
            <template #[`item.priorityId`]="{ item }">
              <v-select
                v-model="item.priorityId"
                :items="priorityOptions"
                item-text="name"
                item-value="id"
                dense
                outlined
                hide-details
                aria-label="Priority"
                class="my-2"
              >
                <template #selection="{ item: priority }">
                  <v-chip x-small label dark :color="priorityColor(priority.id)">
                    {{ priority.name }}
                  </v-chip>
                </template>
              </v-select>
            </template>
            <template #[`item.dueDate`]="{ item }">
              <v-menu
                v-model="item.dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290"
                max-width="290"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="item.dueDate ? formatDate(item.dueDate, 'DD/MM/YYYY') : ''"
                    label="Date"
                    append-icon="mdi-calendar-outline"
                    dense
                    outlined
                    readonly
                    hide-details
                    aria-label="Due date"
                    class="my-2 due-date-input"
                    @click:append="item.dateMenu = true"
                    v-bind="attrs"
                    v-on="on"
                    data-cy="follow-up-create-due-date"
                  />
                </template>
                <v-date-picker
                  v-model="item.dueDate"
                  :min="minimumDueDate"
                  color="primary"
                  no-title
                  @input="item.dateMenu = false"
                />
              </v-menu>
            </template>
            <template #[`item.dueTime`]="{ item }">
              <v-menu
                v-model="item.timeMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290"
                max-width="290"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formatTime(item.dueTime)"
                    label="Time"
                    append-icon="mdi-clock-outline"
                    dense
                    outlined
                    readonly
                    hide-details
                    :error="isPastDueAt(item.dueDate, item.dueTime)"
                    aria-label="Due time"
                    class="my-2 due-time-input"
                    @click:append="item.timeMenu = true"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-time-picker
                  v-if="item.timeMenu"
                  v-model="item.dueTime"
                  color="primary"
                  format="ampm"
                  full-width
                  :min="minimumDueTime(item.dueDate)"
                  @click:minute="item.timeMenu = false"
                />
              </v-menu>
              <div
                v-if="isPastDueAt(item.dueDate, item.dueTime)"
                class="caption error--text mt-n1 mb-1"
              >
                Choose a current or future date and time.
              </div>
            </template>
          </v-data-table>
          <v-textarea v-model="creationAssignmentNote" label="Assignment note (optional)" placeholder="Add context for the receiving user" outlined rows="2" counter="2000" maxlength="2000" class="mt-4" />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-6 py-4">
          <span class="body-2 grey--text text--darken-1">
            {{ globalMode ? creationCountLabel : `${selectedSettings.length} selected` }}
          </span>
          <v-spacer />
          <v-btn text class="text-capitalize" :disabled="saving" @click="createDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            class="text-capitalize"
            :loading="saving"
            :disabled="!canCreateSelected"
            @click="addSelectedActions"
          >
            Create Selected
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FollowUpAssignmentDialog
      v-model="assignmentDialog"
      :action="assignmentAction"
      :initial-mode="assignmentMode"
      :user-id="user.id"
      :read-only="readOnly"
      :saving="!!savingAssignmentId"
      :error="assignmentError"
      :assignees="assigneeOptions"
      :modules="options.modules || []"
      :assignees-loading="assigneesLoading"
      :assignees-error="assigneesError"
      @respond="respondAssignment"
      @assign="assignAction"
      @retry-assignees="loadAssignees"
      @comments="assignmentDialog = false; openComments($event)"
    />

    <v-dialog v-model="editDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>Edit Follow-Up Action</v-card-title>
        <v-card-subtitle v-if="editingAction" class="pt-1">
          {{ editingAction.actionType.name }}
        </v-card-subtitle>
        <v-card-text>
          <v-alert v-if="editError" type="error" text dense>{{ editError }}</v-alert>
          <v-select
            v-model="editForm.priorityId"
            :items="priorityOptions"
            item-text="name"
            item-value="id"
            label="Priority"
            outlined
          />
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editForm.dueDate"
                type="date"
                label="Due Date"
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu
                v-model="editTimeMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290"
                max-width="290"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formatTime(editForm.dueTime)"
                    label="Due Time"
                    prepend-inner-icon="mdi-clock-outline"
                    outlined
                    readonly
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-time-picker
                  v-if="editTimeMenu"
                  v-model="editForm.dueTime"
                  color="primary"
                  format="ampm"
                  full-width
                  @click:minute="editTimeMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text class="text-capitalize" :disabled="saving" @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            class="text-capitalize"
            :loading="saving"
            :disabled="!editForm.priorityId || !editForm.dueDate || !editForm.dueTime"
            @click="updateAction"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="commentsDialog" max-width="680">
      <v-card v-if="selectedAction" class="comments-card">
        <v-card-title class="pb-1 d-flex align-center">
          <span>{{ selectedAction.actionType.name }}</span>
          <v-spacer />
          <v-btn icon @click="commentsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle class="pt-1">
          Comments for this Follow-Up Action
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="comments-list pa-5">
          <div v-if="!selectedAction.comments.length" class="text-center py-8 grey--text">
            <v-icon color="grey lighten-1" size="42">mdi-comment-text-outline</v-icon>
            <div class="mt-2">No comments have been added.</div>
          </div>
          <div v-else>
            <div
              v-for="comment in selectedAction.comments"
              :key="comment.id"
              class="comment-item pa-3 mb-3"
            >
              <div class="d-flex align-start mb-2">
                <span class="font-weight-medium">{{ commentAuthor(comment) }}</span>
                <v-spacer />
                <v-menu v-if="canManageComment(comment)" offset-y left>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      aria-label="Comment actions"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="openEditComment(comment)">
                      <v-list-item-icon class="mr-3">
                        <v-icon small color="primary">mdi-pencil-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="confirmDeleteComment(comment)">
                      <v-list-item-icon class="mr-3">
                        <v-icon small color="error">mdi-delete-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title class="error--text">Delete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div class="body-2 comment-text">{{ comment.comment }}</div>
              <div class="caption grey--text text-right mt-2">
                {{ formatDate(comment.dateCreated, 'MMM D, YYYY [at] h:mm A') }}
              </div>
            </div>
          </div>
        </v-card-text>

        <template v-if="!readOnly">
          <v-divider />
          <div class="comment-composer pa-4">
            <v-textarea
              v-model="newComment"
              label="Add a comment"
              placeholder="Enter an update or note..."
              outlined
              rows="2"
              hide-details
              counter="4000"
              maxlength="4000"
            />
            <div class="d-flex justify-end mt-3">
              <v-btn
                color="primary"
                depressed
                small
                class="text-capitalize"
                :loading="savingComment"
                :disabled="!newComment.trim()"
                @click="addComment"
              >
                <v-icon small left>mdi-comment-plus-outline</v-icon>
                Add Comment
              </v-btn>
            </div>
          </div>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editCommentDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>Edit Comment</v-card-title>
        <v-card-text>
          <v-alert v-if="commentError" type="error" text dense>{{ commentError }}</v-alert>
          <v-textarea
            v-model="editCommentText"
            label="Comment"
            outlined
            rows="4"
            counter="4000"
            maxlength="4000"
            hide-details
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text class="text-capitalize" :disabled="savingCommentEdit" @click="editCommentDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            class="text-capitalize"
            :loading="savingCommentEdit"
            :disabled="!editCommentText.trim()"
            @click="updateComment"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import dayjs from 'dayjs';
import { EventBus } from '@/event-bus';
import { FOLLOW_UP_ACTIONS_CHANGED, FOLLOW_UP_ACTION_QUEUES } from '@/lib/constants/followUpActions';
import CoreMixin from '@/mixins/core.mixin';
import FormatMixin from '@/mixins/format.mixin';
import ConfirmationModal from '@/modules/shared/components/core/modals/ConfirmationModal.vue';
import { FollowUpActionsService } from '@/modules/shared/services/follow-up-actions.service';
import FollowUpAssignmentDialog from './FollowUpAssignmentDialog.vue';
import { assignmentLabel, assignmentStatus, canCancelAssignment, canEditAction, canReassignAction, canRespondToAssignment, isActiveAction } from './assignment-utils';

const service = new FollowUpActionsService();

export default {
  name: 'FollowUpActionsPanel',
  components: { ConfirmationModal, FollowUpAssignmentDialog },
  mixins: [CoreMixin, FormatMixin],
  props: {
    globalMode: { type: Boolean, default: false },
    initialQueue: { type: String, default: 'active', validator: value => FOLLOW_UP_ACTION_QUEUES.some(queue => queue.value === value) },
    focusActionId: { type: String, default: null },
    module: { type: String, default: '' },
    recordType: { type: String, default: '' },
    recordId: { type: String, default: '' },
    recordLabel: { type: String, default: '' },
    readOnly: { type: Boolean, default: false }
  },
  data() { return {
    queues: FOLLOW_UP_ACTION_QUEUES,
    manageQueue: this.initialQueue,
    queueCounts: { active: 0, incoming: 0, sent: 0, rejected: 0, history: 0 },
    assignees: [],
    assigneesLoading: false,
    assigneesError: '',
    creationAssignmentNote: '',
    assignmentDialog: false,
    assignmentMode: 'details',
    assignmentAction: null,
    assignmentError: '',
    savingAssignmentId: null,
    loading: false,
    saving: false,
    savingComment: false,
    savingCommentEdit: false,
    savingStatusId: null,
    error: '',
    formError: '',
    editError: '',
    commentError: '',
    actions: [],
    manageSearch: '',
    manageSearchQuery: '',
    manageSearchTimer: null,
    manageRequestId: 0,
    manageTotal: 0,
    manageActiveCount: 0,
    manageTableOptions: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
    manageFiltersOpen: false,
    manageFilters: { module: [], actionType: [], status: [], priority: [], dueDate: [], dueFrom: '', dueTo: '' },
    manageDueDateOptions: [
      { text: 'Today', value: 'today' },
      { text: 'Tomorrow', value: 'tomorrow' },
      { text: 'This Week', value: 'this_week' },
      { text: 'Overdue', value: 'overdue' },
      { text: 'Custom Date Range', value: 'custom' }
    ],
    options: { enabled: true, moduleId: '', priorities: [], actionTypes: [] },
    createDialog: false,
    editDialog: false,
    editTimeMenu: false,
    commentsDialog: false,
    editCommentDialog: false,
    selectedAction: null,
    editingComment: null,
    newComment: '',
    editCommentText: '',
    selectedSettings: [],
    creationRows: [],
    editingAction: null,
    editForm: { priorityId: '', dueDate: '', dueTime: '' },
    statuses: [
      { text: 'Pending', value: 'pending' },
      { text: 'In Progress', value: 'in_progress' },
      { text: 'Completed', value: 'completed' },
      { text: 'Cancelled', value: 'cancelled' }
    ],
    headers: [
      { text: 'Name', value: 'actionType', width: '26%' },
      { text: 'Priority', value: 'priority', width: '12%' },
      { text: 'Due Date', value: 'dueAt', width: '18%' },
      { text: 'Status', value: 'status', width: '14%' },
      { text: 'Comments', value: 'comments', sortable: false },
      { text: 'Action', value: 'actions', align: 'end', sortable: false, width: '190px' }
    ],
    selectionHeaders: [
      { text: 'Name', value: 'name', width: '240px' },
      { text: 'Priority', value: 'priorityId', width: '180px', sortable: false },
      { text: 'Due Date', value: 'dueDate', width: '190px', sortable: false },
      { text: 'Due Time', value: 'dueTime', width: '190px', sortable: false }
    ]
  }; },
  computed: {
    currentQueue() { return this.queues.find(queue => queue.value === this.manageQueue) || this.queues[0]; },
    assigneeOptions() {
      const users = this.assignees.some(user => user.id === this.user?.id) ? this.assignees : [this.user, ...this.assignees];
      return users.filter(user => user?.id).map(user => ({ ...user, name: `${user.username || user.email || 'User'}${user.id === this.user?.id ? ' (you)' : ''}` }));
    },
    activeActionCount() {
      return this.globalMode
        ? this.manageActiveCount
        : this.actions.filter(isActiveAction).length;
    },
    creationHeaders() {
      const selection = { text: '', value: 'data-table-select', width: '48px', sortable: false };
      const assignee = { text: 'Assign to', value: 'assignedToId', sortable: false };
      if (!this.globalMode) return [selection, this.selectionHeaders[0], assignee, ...this.selectionHeaders.slice(1)];
      return [
        selection,
        this.selectionHeaders[0],
        { text: 'Modules', value: 'selectedModuleIds', sortable: false },
        assignee,
        ...this.selectionHeaders.slice(1)
      ];
    },
    creationModules() {
      return (this.options.modules || []).filter(module =>
        module.id !== 'Global' && module.enabled && module.available !== false
      );
    },
    manageModuleOptions() {
      const configured = this.options.modules || [];
      const missing = this.actions
        .flatMap(action => action.moduleIds?.length ? action.moduleIds : [action.module])
        .filter((id, index, ids) => ids.indexOf(id) === index)
        .filter(id => id && !configured.some(module => module.id === id))
        .map(id => ({ id, name: this.moduleName(id) }));
      return [...configured, ...missing];
    },
    manageActionTypeOptions() {
      const configured = this.options.filterActionTypes || this.options.actionTypes || [];
      const missing = this.actions
        .map(action => action.actionType)
        .filter((type, index, types) => types.findIndex(item => item.id === type.id) === index)
        .filter(type => !configured.some(item => item.id === type.id));
      return [...configured, ...missing];
    },
    manageFilterCount() {
      return ['module', 'actionType', 'status', 'priority', 'dueDate']
        .filter(key => (this.manageFilters[key] || []).length).length;
    },
    displayActions() {
      return this.actions;
    },
    manageQueryKey() {
      return JSON.stringify({
        queue: this.manageQueue,
        page: this.manageTableOptions.page,
        pageSize: this.manageTableOptions.itemsPerPage,
        sortBy: this.manageTableOptions.sortBy,
        sortDesc: this.manageTableOptions.sortDesc,
        search: this.manageSearchQuery,
        filters: this.manageFilters,
        focusActionId: this.focusActionId
      });
    },
    tableHeaders() {
      const headers = [this.headers[0], { text: 'Assignment / owner', value: 'assignment', width: '180px', sortable: false }, ...this.headers.slice(1)];
      if (!this.globalMode) return headers;
      const queueHeaders = ['incoming', 'rejected'].includes(this.manageQueue) ? headers.slice(1).filter(header => !['status', 'comments'].includes(header.value)) : headers.slice(1);
      return [
        this.headers[0],
        { text: 'Modules', value: 'module', width: '140px' },
        { text: 'Related Record', value: 'relatedRecordLabel', width: '180px' },
        ...queueHeaders.filter(header => header.value !== 'actions'),
        ...(this.manageQueue === 'rejected' ? [{ text: 'Rejection reason', value: 'rejectionReason', width: '240px', sortable: false }] : []),
        ...queueHeaders.filter(header => header.value === 'actions').map(header => this.manageQueue === 'rejected' ? { ...header, width: '240px' } : header)
      ];
    },
    priorityOptions() {
      return this.options.priorities.length
        ? this.options.priorities
        : ['low', 'medium', 'high', 'urgent'].map(id => ({ id, name: this.priorityName(id) }));
    },
    creationActionCount() {
      return this.selectedSettings.length;
    },
    creationModuleCount() {
      return this.selectedSettings.reduce(
        (total, setting) => total + (setting.selectedModuleIds?.length || 0),
        0
      );
    },
    creationCountLabel() {
      return `${this.creationActionCount} ${this.creationActionCount === 1 ? 'action' : 'actions'} to create`;
    },
    canCreateSelected() {
      return (
        this.selectedSettings.length > 0 &&
        this.creationAssignmentNote.length <= 2000 &&
        (!this.globalMode || this.creationModuleCount <= 100) &&
        this.selectedSettings.every(
          setting =>
            (!this.globalMode || setting.selectedModuleIds.length > 0) &&
            setting.priorityId &&
            setting.assignedToId &&
            setting.dueDate &&
            setting.dueTime &&
            !this.isPastDueAt(setting.dueDate, setting.dueTime)
        )
      );
    },
    minimumDueDate() {
      return dayjs().format('YYYY-MM-DD');
    }
  },
  watch: {
    initialQueue(value) { this.manageQueue = value; },
    manageQueue() { this.manageTableOptions.page = 1; this.manageFilters.status = []; },
    recordId() {
      if (!this.globalMode) this.load();
    },
    manageSearch(value) {
      clearTimeout(this.manageSearchTimer);
      this.manageSearchTimer = setTimeout(() => {
        this.manageTableOptions.page = 1;
        this.manageSearchQuery = String(value || '').trim();
      }, 300);
    },
    manageFilters: {
      deep: true,
      handler() {
        this.manageTableOptions.page = 1;
      }
    },
    manageQueryKey: {
      immediate: true,
      handler() {
        if (this.globalMode) this.loadAssignedPage();
      }
    }
  },
  mounted() {
    EventBus.$on(FOLLOW_UP_ACTIONS_CHANGED, this.handleActionsChanged);
    if (!this.globalMode) this.load();
  },
  beforeDestroy() {
    EventBus.$off(FOLLOW_UP_ACTIONS_CHANGED, this.handleActionsChanged);
    clearTimeout(this.manageSearchTimer);
    this.manageRequestId += 1;
  },
  methods: {
    assignmentLabel,
    descriptionPreview(description) {
      const words = (description || '').trim().split(/\s+/);
      return words.length > 5 ? `${words.slice(0, 5).join(' ').replace(/[.,;:!?…]+$/, '')}...` : words.join(' ');
    },
    canRespond(action) { return !this.readOnly && canRespondToAssignment(action, this.user?.id); },
    canReassign(action) { return !this.readOnly && canReassignAction(action, this.user?.id); },
    canCancelAssignment(action) { return !this.readOnly && canCancelAssignment(action, this.user?.id); },
    canEdit(action) { return !this.readOnly && canEditAction(action, this.user?.id); },
    assignmentColor(action) { return { accepted: 'success', awaiting_acceptance: 'orange darken-2', rejected: 'error' }[assignmentStatus(action)]; },
    userName(user) { return user?.id === this.user?.id ? 'You' : user?.username || user?.email || 'User'; },
    handleActionsChanged(event) { if (event?.source !== this._uid) this.load(); },
    async loadAssignees() {
      if (this.assigneesLoading) return;
      this.assigneesLoading = true;
      this.assigneesError = '';
      try { const { data } = await service.getAssignees(); this.assignees = data; }
      catch { this.assigneesError = 'Unable to load users for assignment.'; }
      finally { this.assigneesLoading = false; }
    },
    openAssignment(action, mode = 'details') {
      this.assignmentAction = action; this.assignmentMode = mode; this.assignmentError = ''; this.assignmentDialog = true;
      if (mode === 'assign') this.loadAssignees();
    },
    async respondAssignment({ action, response, reason }) {
      if (!this.canRespond(action) || this.savingAssignmentId) return;
      this.savingAssignmentId = action.id; this.assignmentError = '';
      try {
        const { data } = await service.respondAssignment(action, response, reason);
        if (this.globalMode) await this.loadAssignedPage(); else this.replaceAction(data);
        this.assignmentDialog = false; this.notifyAssignedActionsChanged();
        this.showSnack({ color: 'success', title: response === 'accept' ? 'Action accepted' : 'Action returned', message: response === 'accept' ? 'Added to My Actions as Pending.' : 'Returned to the assigner in their Rejected section.' });
      } catch (error) { this.assignmentError = this.requestMessage(error); this.showRequestError(error, 'Unable to respond to assignment'); }
      finally { this.savingAssignmentId = null; }
    },
    async assignAction({ action, assignedToId, assignmentNote }) {
      if (!this.canReassign(action) || this.savingAssignmentId) return;
      this.savingAssignmentId = action.id; this.assignmentError = '';
      try {
        const { data } = await service.assign(action, assignedToId, assignmentNote);
        if (this.globalMode) await this.loadAssignedPage(); else this.replaceAction(data);
        this.assignmentDialog = false; this.notifyAssignedActionsChanged();
        this.showSnack({ color: 'success', title: 'Action assigned', message: assignedToId === this.user.id ? 'Added to My Actions as Pending.' : 'Assignment sent for acceptance.' });
      } catch (error) { this.assignmentError = this.requestMessage(error); this.showRequestError(error, 'Unable to assign action'); }
      finally { this.savingAssignmentId = null; }
    },
    confirmCancelAssignment(action) {
      if (!this.canCancelAssignment(action) || this.savingAssignmentId) return;
      this.$refs.cancelAssignmentConfirmation.show(async () => {
        if (!this.canCancelAssignment(action) || this.savingAssignmentId) return;
        this.savingAssignmentId = action.id;
        try {
          const { data } = await service.cancelAssignment(action);
          if (this.globalMode) await this.loadAssignedPage(); else this.replaceAction(data);
          this.notifyAssignedActionsChanged();
          this.showSnack({ color: 'success', title: 'Action cancelled', message: 'Follow-Up Action moved to History.' });
        } catch (error) {
          this.showRequestError(error, 'Unable to cancel action');
          if (error.response?.status === 409) await this.load();
          throw error;
        } finally {
          this.savingAssignmentId = null;
        }
      });
    },
    requestMessage(error) { const message = error.response?.data?.message; return Array.isArray(message) ? message[0] : message || 'Please try again.'; },
    clearManageFilters() {
      this.manageFilters = { module: [], actionType: [], status: [], priority: [], dueDate: [], dueFrom: '', dueTo: '' };
    },
    async loadAssignedPage() {
      const requestId = ++this.manageRequestId;
      const selected = key => (this.manageFilters[key] || []).join(',') || undefined;
      const today = dayjs();
      const params = {
        queue: this.manageQueue,
        page: this.manageTableOptions.page,
        pageSize: this.manageTableOptions.itemsPerPage,
        sortBy: this.manageTableOptions.sortBy?.[0] || undefined,
        sortOrder: this.manageTableOptions.sortDesc?.[0] ? 'desc' : 'asc',
        search: this.manageSearchQuery || undefined,
        modules: selected('module'),
        actionTypes: selected('actionType'),
        statuses: selected('status'),
        priorities: selected('priority'),
        dueDates: selected('dueDate'),
        todayStart: today.startOf('day').toISOString(),
        todayEnd: today.endOf('day').toISOString(),
        tomorrowStart: today.add(1, 'day').startOf('day').toISOString(),
        tomorrowEnd: today.add(1, 'day').endOf('day').toISOString(),
        weekStart: today.startOf('week').toISOString(),
        weekEnd: today.endOf('week').toISOString(),
        dueFrom: (this.manageFilters.dueDate || []).includes('custom') && this.manageFilters.dueFrom
          ? dayjs(this.manageFilters.dueFrom).startOf('day').toISOString() : undefined,
        dueTo: (this.manageFilters.dueDate || []).includes('custom') && this.manageFilters.dueTo
          ? dayjs(this.manageFilters.dueTo).endOf('day').toISOString() : undefined,
        now: today.toISOString(),
        focusActionId: this.focusActionId || undefined
      };
      this.loading = true;
      this.error = '';
      try {
        const { data } = await service.getAssignedPage(params);
        if (requestId !== this.manageRequestId) return;
        this.options = { enabled: true, ...data.options };
        this.actions = data.actions;
        this.manageTotal = data.total;
        this.manageActiveCount = data.activeCount;
        this.queueCounts = data.queueCounts || { ...this.queueCounts, active: data.activeCount };
        if (data.page !== this.manageTableOptions.page) this.manageTableOptions.page = data.page;
        this.syncSelectedAction();
      } catch (error) {
        if (requestId === this.manageRequestId) this.error = 'Unable to load Follow-Up Actions. Please try again.';
      } finally {
        if (requestId === this.manageRequestId) this.loading = false;
      }
    },
    async load() {
      if (this.globalMode) return this.loadAssignedPage();
      if (!this.recordId) return;
      this.loading = true;
      this.error = '';
      try {
        const [{ data: options }, { data: actions }] = await Promise.all([
          service.getOptions(this.module),
          service.getForContext({
            module: this.module,
            relatedRecordType: this.recordType,
            relatedRecordId: this.recordId
          })
        ]);
        this.options = options;
        this.actions = actions;
        this.syncSelectedAction();
      } catch (error) {
        this.error = 'Unable to load Follow-Up Actions. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.loadAssignees();
      this.creationAssignmentNote = '';
      const currentDateTime = dayjs();
      this.selectedSettings = [];
      this.creationRows = this.options.actionTypes.map(setting => ({
        ...setting,
        assignedToId: this.user.id,
        selectedModuleIds: [],
        priorityId: setting.defaultPriorityId || 'medium',
        dueDate: currentDateTime
          .add(setting.defaultTimelineDays ?? 0, 'day')
          .format('YYYY-MM-DD'),
        dueTime: currentDateTime.format('HH:mm'),
        dateMenu: false,
        timeMenu: false
      }));
      this.formError = '';
      this.createDialog = true;
    },
    applicableModules(setting) {
      return this.creationModules.filter(module => setting.moduleIds.includes(module.id));
    },
    async addSelectedActions() {
      this.formError = '';
      if (!this.selectedSettings.length) return;
      if (this.selectedSettings.some(setting => this.isPastDueAt(setting.dueDate, setting.dueTime))) {
        this.formError = 'Due date and time cannot be in the past.';
        return;
      }
      this.saving = true;
      try {
        const selections = this.selectedSettings.map(setting => ({
          settingId: setting.id,
          priorityId: setting.priorityId,
          dueAt: this.toDueAt(setting.dueDate, setting.dueTime),
          assignedToId: setting.assignedToId,
          assignmentNote: this.creationAssignmentNote.trim(),
          ...(this.globalMode ? { moduleIds: setting.selectedModuleIds } : {})
        }));
        const { data } = this.globalMode
          ? await service.createGlobalBatch({ actions: selections })
          : await service.createBatch({
            actions: selections,
            module: this.module,
            relatedRecordType: this.recordType,
            relatedRecordId: this.recordId,
            relatedRecordLabel: this.recordLabel
          });
        if (this.globalMode) {
          this.manageQueue = selections.some(selection => selection.assignedToId !== this.user.id) ? 'sent' : 'active';
          await this.loadAssignedPage();
        } else {
          this.actions.push(...data);
          this.sortActions();
        }
        this.notifyAssignedActionsChanged();
        this.createDialog = false;
        this.showSnack({
          color: 'success',
          title: 'Actions added',
          message: `${data.length} Follow-Up ${data.length === 1 ? 'Action was' : 'Actions were'} successfully added.`
        });
      } catch (error) {
        this.formError = error.response?.data?.message || 'Unable to add the selected Follow-Up Actions.';
      } finally {
        this.saving = false;
      }
    },
    openEditDialog(action) {
      this.editingAction = action;
      this.editForm = {
        priorityId: action.priority.id,
        dueDate: dayjs(action.dueAt).format('YYYY-MM-DD'),
        dueTime: dayjs(action.dueAt).format('HH:mm')
      };
      this.editTimeMenu = false;
      this.editError = '';
      this.editDialog = true;
    },
    async updateAction() {
      if (!this.editingAction || !this.editForm.priorityId || !this.editForm.dueDate || !this.editForm.dueTime)
        return;
      this.saving = true;
      this.editError = '';
      try {
        const { data } = await service.update(this.editingAction.id, {
          priorityId: this.editForm.priorityId,
          dueAt: this.toDueAt(this.editForm.dueDate, this.editForm.dueTime)
        });
        if (this.globalMode) await this.loadAssignedPage();
        else {
          this.replaceAction(data);
          this.sortActions();
        }
        this.notifyAssignedActionsChanged();
        this.editDialog = false;
        this.showSnack({
          color: 'success',
          title: 'Action updated',
          message: 'Follow-Up Action was successfully updated.'
        });
      } catch (error) {
        const message = error.response?.data?.message;
        this.editError = Array.isArray(message) ? message[0] : message || 'Unable to update the Follow-Up Action.';
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(action) {
      this.$refs.deleteConfirmation.show(async () => {
        try {
          await service.delete(action.id);
          if (this.globalMode) await this.loadAssignedPage();
          else this.actions = this.actions.filter(item => item.id !== action.id);
          this.notifyAssignedActionsChanged();
          if (this.selectedAction?.id === action.id) {
            this.selectedAction = null;
            this.commentsDialog = false;
          }
          this.showSnack({
            color: 'success',
            title: 'Action deleted',
            message: 'Follow-Up Action was successfully deleted.'
          });
        } catch (error) {
          this.showRequestError(error, 'Unable to delete action');
          throw error;
        }
      });
    },
    async changeStatus(action, status) {
      this.savingStatusId = action.id;
      try {
        const { data } = await service.updateStatus(action.id, status);
        if (this.globalMode) await this.loadAssignedPage();
        else this.replaceAction(data);
        this.notifyAssignedActionsChanged();
        this.showSnack({ color: 'success', title: 'Updated', message: 'Follow-Up Action status was successfully updated.' });
      } catch (error) {
        this.showRequestError(error, 'Unable to update status');
      } finally {
        this.savingStatusId = null;
      }
    },
    openComments(action) {
      this.selectedAction = action;
      this.newComment = '';
      this.commentsDialog = true;
    },
    async addComment() {
      const comment = this.newComment.trim();
      if (!comment || comment.length > 4000) return;
      this.savingComment = true;
      try {
        const { data } = await service.addComment(this.selectedAction.id, comment);
        this.replaceAction(data);
        this.selectedAction = data;
        this.newComment = '';
        this.showSnack({ color: 'success', title: 'Comment added', message: 'Your comment was added successfully.' });
      } catch (error) {
        this.showRequestError(error, 'Unable to add comment');
      } finally {
        this.savingComment = false;
      }
    },
    canManageComment(comment) {
      return !this.readOnly && (comment.userId === this.user?.id || comment.user?.id === this.user?.id);
    },
    openEditComment(comment) {
      this.editingComment = comment;
      this.editCommentText = comment.comment;
      this.commentError = '';
      this.editCommentDialog = true;
    },
    async updateComment() {
      const comment = this.editCommentText.trim();
      if (!this.editingComment || !comment || comment.length > 4000) return;
      this.savingCommentEdit = true;
      this.commentError = '';
      try {
        const { data } = await service.updateComment(
          this.selectedAction.id,
          this.editingComment.id,
          comment
        );
        this.replaceAction(data);
        this.selectedAction = data;
        this.editCommentDialog = false;
        this.showSnack({
          color: 'success',
          title: 'Comment updated',
          message: 'Your comment was updated successfully.'
        });
      } catch (error) {
        const message = error.response?.data?.message;
        this.commentError = Array.isArray(message) ? message[0] : message || 'Unable to update the comment.';
      } finally {
        this.savingCommentEdit = false;
      }
    },
    confirmDeleteComment(comment) {
      this.$refs.deleteCommentConfirmation.show(async () => {
        try {
          const { data } = await service.deleteComment(this.selectedAction.id, comment.id);
          this.replaceAction(data);
          this.selectedAction = data;
          this.showSnack({
            color: 'success',
            title: 'Comment deleted',
            message: 'Your comment was deleted successfully.'
          });
        } catch (error) {
          this.showRequestError(error, 'Unable to delete comment');
          throw error;
        }
      });
    },
    replaceAction(action) {
      this.actions = this.actions.map(item => (item.id === action.id ? action : item));
      this.syncSelectedAction();
    },
    notifyAssignedActionsChanged() {
      EventBus.$emit(FOLLOW_UP_ACTIONS_CHANGED, { source: this._uid });
    },
    syncSelectedAction() {
      if (!this.selectedAction) return;
      this.selectedAction = this.actions.find(item => item.id === this.selectedAction.id) || (this.globalMode ? this.selectedAction : null);
    },
    sortActions() {
      this.actions.sort((left, right) => new Date(left.dueAt) - new Date(right.dueAt));
    },
    isOverdue(action) {
      return isActiveAction(action) && dayjs(action.dueAt).isBefore(dayjs());
    },
    priorityColor(priority) {
      return { low: 'blue-grey', medium: 'amber darken-2', high: 'deep-orange', urgent: 'red darken-2' }[priority] || 'grey';
    },
    priorityName(priority) {
      return { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' }[priority] || priority;
    },
    moduleName(moduleId) {
      return this.options.modules?.find(module => module.id === moduleId)?.name || moduleId;
    },
    timelineLabel(days) {
      if (days === 1) return 'Tomorrow';
      if (!days) return 'Today';
      return `${days} days`;
    },
    formatTime(time) {
      if (!time) return '';
      return dayjs(`2000-01-01T${time}`).format('h:mm A');
    },
    minimumDueTime(date) {
      return date === dayjs().format('YYYY-MM-DD') ? dayjs().format('HH:mm') : undefined;
    },
    isPastDueAt(date, time) {
      if (!date || !time) return false;
      return dayjs(`${date}T${time}:00`).isBefore(dayjs().startOf('minute'));
    },
    toDueAt(date, time) {
      return dayjs(`${date}T${time}:00`).toISOString();
    },
    statusName(status) {
      return this.statuses.find(item => item.value === status)?.text || status;
    },
    statusColor(status) {
      return { pending: 'blue-grey', in_progress: 'primary', completed: 'success', cancelled: 'grey darken-1' }[status] || 'grey';
    },
    commentAuthor(comment) {
      return comment.user?.username || comment.user?.email || 'User';
    },
    showRequestError(error, title) {
      const responseMessage = error.response?.data?.message;
      this.showSnack({
        color: 'error',
        title,
        message: Array.isArray(responseMessage)
          ? responseMessage[0]
          : responseMessage || 'Please try again.'
      });
    }
  }
};
</script>

<style scoped>
.manage-tabs-row { display: flex; align-items: center; flex: 0 0 auto; gap: 16px; padding-right: 16px; }
.manage-queue-tabs { flex: 1 1 0; min-width: 0; width: auto; }
.manage-active-message { flex: 0 0 auto; margin: 0; color: #027a48; font-size: 13px; font-weight: 600; white-space: nowrap; }
.assignment-cell { min-width: 150px; }
.assignment-row-actions { gap: 6px; min-width: 170px; }
.assignment-utility-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.assignment-reason { min-width: 180px; max-width: 300px; white-space: pre-wrap; overflow-wrap: anywhere; }
.follow-up-card {
  border: 1px solid #e4e7ec;
}
.global-follow-up-container {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  padding: 0 !important;
}
.global-follow-up-container .follow-up-card {
  border: 0;
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}
.min-width-0 {
  min-width: 0;
}
.manage-header {
  flex: 0 0 auto;
  background: #fff;
}
.manage-title {
  color: #182230;
  letter-spacing: -0.02em;
}
.manage-close-button {
  color: #667085 !important;
}
.manage-toolbar {
  flex: 0 0 auto;
  background: #fbfcfe;
  border-bottom: 1px solid #e9edf3;
}
.manage-search {
  flex: 0 1 400px;
  max-width: 400px;
}
.manage-filter-button {
  flex: 0 0 auto;
}
.manage-filter-scrim {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.42);
  z-index: 3;
}
.manage-filter-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 100%);
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: -12px 0 36px rgba(16, 24, 40, 0.12);
  z-index: 4;
}
.manage-filter-slide-enter-active,
.manage-filter-slide-leave-active {
  transition: transform 180ms ease;
}
.manage-filter-slide-enter,
.manage-filter-slide-leave-to {
  transform: translateX(100%);
}
.manage-filter-header,
.manage-filter-footer {
  flex: 0 0 auto;
  border-bottom: 1px solid #e9edf3;
}
.manage-filter-footer {
  border-top: 1px solid #e9edf3;
  border-bottom: 0;
}
.manage-filter-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.manage-filter-date-range {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.global-follow-up-container .follow-up-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}
.global-follow-up-container .follow-up-table ::v-deep .v-data-table__wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.global-follow-up-container .follow-up-table ::v-deep table {
  min-width: 1080px;
}
.global-follow-up-container .follow-up-table ::v-deep th {
  background: #f8fafc;
  border-bottom: 1px solid #e9edf3 !important;
  color: #667085 !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}
.global-follow-up-container .follow-up-table ::v-deep td {
  border-bottom: 1px solid #f0f2f5 !important;
  color: #344054;
  font-size: 13px;
  height: 70px;
}
.manage-module-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 130px;
}
.global-follow-up-container .follow-up-table ::v-deep tbody tr:hover {
  background: #f8fafc !important;
}
.global-follow-up-container .follow-up-table ::v-deep .v-data-footer {
  flex: 0 0 auto;
  background: #fff;
  border-top: 1px solid #e9edf3;
  color: #667085;
  min-height: 58px;
  padding: 0 24px;
}
.follow-up-table ::v-deep th {
  background: #fafafa;
  color: #475467 !important;
  font-weight: 600;
}
.action-name-cell,
.selection-name-cell {
  max-width: 360px;
  min-width: 0;
}
.selection-table:not(.v-data-table--mobile) ::v-deep table {
  width: 100%;
  table-layout: fixed;
}
.selection-name-cell {
  overflow-wrap: anywhere;
}
.selection-table.v-data-table--mobile ::v-deep .v-data-table__mobile-row {
  flex-wrap: wrap;
}
.selection-table.v-data-table--mobile ::v-deep .v-data-table__mobile-row__cell {
  width: 100%;
  min-width: 0;
  text-align: left;
}
.action-description,
.selection-description {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.due-date-input,
.due-time-input {
  min-width: 155px;
}
.due-date-input ::v-deep input,
.due-time-input ::v-deep input {
  min-width: 86px;
  text-overflow: clip;
}
.comment-item {
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}
.comments-card {
  display: flex;
  flex-direction: column;
  height: min(720px, calc(100vh - 96px));
}
.comments-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.comment-composer {
  flex: 0 0 auto;
  background: #fff;
}
.comment-text {
  white-space: pre-wrap;
  word-break: break-word;
}
@media (max-width: 600px) {
  .manage-header {
    padding: 16px !important;
  }
  .manage-title {
    font-size: 18px !important;
  }
  .manage-tabs-row { gap: 8px; }
  .manage-active-message { max-width: 120px; font-size: 12px; white-space: normal; text-align: right; }
  .manage-create-button {
    min-width: 0 !important;
    padding: 0 10px !important;
  }
  .manage-toolbar {
    flex-wrap: wrap;
    padding: 12px 16px !important;
  }
  .manage-search {
    flex: 1 1 100%;
    max-width: none;
  }
  .manage-filter-button {
    margin: 12px 0 0 !important;
  }
  .manage-toolbar > .caption {
    margin-top: 12px;
  }
}
</style>
